import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/store/authStore';
import Button from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { RiUserLine, RiArrowRightLine, RiUploadCloud2Line, RiAddLine, RiPencilLine, RiCloseLine, RiPaletteLine, RiFilePdf2Fill, RiFileImageFill, RiFileTextLine, RiDeleteBinLine, RiCheckboxCircleLine, RiMapPinLine, RiCheckboxFill, RiCheckboxBlankLine, RiTeamLine, RiMailLine, RiHourglass2Fill } from '@remixicon/react';
import { HexColorPicker } from 'react-colorful';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { encodeData, decodeData } from '@/utils';

// Simple Label component
const Label = ({ children, className, htmlFor }) => (
  <label htmlFor={htmlFor} className={`block text-sm font-medium text-gray-700 ${className || ''}`}>
    {children}
  </label>
);

export default function Onboarding({ isProfile = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteData = decodeData(searchParams.get('d'));
  const isPending = inviteData.progress === 'pending';
  const [firstName, setFirstName] = useState(inviteData.firstName || '');
  const [lastName, setLastName] = useState(inviteData.lastName || '');
  const [userTypes, setUserTypes] = useState(inviteData.userTypes || []);
  const isBroker = userTypes.includes('broker');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [currentStep, setCurrentStep] = useState(isPending ? 'userType' : 'profile');
  const [do85File, setDo85File] = useState(null);
  const [part135File, setPart135File] = useState(null);
  const [brandColor, setBrandColor] = useState(null);
  const [customColorInput, setCustomColorInput] = useState('#000000');
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [contractFile, setContractFile] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMember, setNewMember] = useState({ firstName: '', lastName: '', designation: '', email: '' });
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    phone: '',
    countryCode: '+1',
    contactTitle: '',
    soleProprietorship: false,
  });
  const { logout, setAuthType, userProfile, fetchUserProfile } = useAuthStore();
  const queryClient = useQueryClient();

  const { data: organization, isLoading: isOrgLoading } = useQuery({
    queryKey: ["organization", userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  // Determine if we're still resolving the correct step
  const isResolvingStep = !!userProfile?.org_id && isOrgLoading && !isPending;

  // Populate form data from existing organization and userProfile
  useEffect(() => {
    if (!organization) return;
    setFormData(prev => ({
      ...prev,
      companyName: organization.name || prev.companyName,
      address: organization.address?.street || prev.address,
      phone: userProfile?.phone || prev.phone,
      contactTitle: organization.settings?.pocTitle || prev.contactTitle,
      soleProprietorship: organization.settings?.isSole ?? prev.soleProprietorship,
    }));
    if (organization.branding?.color) setBrandColor(organization.branding.color);
  }, [organization]);

  // Populate name from userProfile
  useEffect(() => {
    if (!userProfile) return;
    if (userProfile.first_name && !firstName) setFirstName(userProfile.first_name);
    if (userProfile.last_name && !lastName) setLastName(userProfile.last_name);
    if (userProfile.role && userTypes.length === 0) setUserTypes([userProfile.role]);
  }, [userProfile]);

  // Set the default step based on missing branding data
  useEffect(() => {
    if (!organization || isPending) return;
    const branding = organization.branding;

    // Check steps in order for the user type
    const profileMissing = !organization?.name || !organization?.address?.street;
    const steps = !isBroker
      ? [
          { step: 'profile', missing: profileMissing },
          { step: 'documents', missing: !branding?.do85Registration || !branding?.part135Certification },
          { step: 'branding', missing: !branding?.color || !branding?.logo },
          { step: 'contract', missing: !branding?.contract },
          // { step: 'team', missing: userProfile?.status !== 'ONBOARDING_COMPLETED' },
          { step: 'review', missing: userProfile?.status !== 'ONBOARDING_COMPLETED' },
        ]
      : [
          { step: 'profile', missing: profileMissing },
          { step: 'branding', missing: !branding?.color || !branding?.logo },
          { step: 'contract', missing: !branding?.contract },
          // { step: 'team', missing: userProfile?.status !== 'ONBOARDING_COMPLETED' },
          { step: 'review', missing: userProfile?.status !== 'ONBOARDING_COMPLETED' },
        ];

    const firstIncomplete = steps.find(s => s.missing);
    if (firstIncomplete) {
      setCurrentStep(firstIncomplete.step);
    }
  }, [organization]);

  useEffect(() => {
    let mounted = true;
    let authSubscription = null;
    let sessionChecked = false;
    console.log("userTypes", userTypes);
    const getCurrentUser = async () => {
      // Check for OAuth errors in URL
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const oauthError = urlParams.get('error') || hashParams.get('error');
      const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');

      if (oauthError) {
        console.error('OAuth error detected:', oauthError, errorDescription);
        toast.error(`Authentication failed: ${decodeURIComponent(errorDescription || oauthError)}`);
        // Don't try to get session if there's an OAuth error
        setTimeout(() => {
          if (mounted) 
            setAuthType('signup');
            navigate('/signup');
        }, 5000);
        return;
      }

      // Listen for auth state changes to handle OAuth callback
      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (!mounted) return;

        console.log('ProfileSetup auth event:', event, 'Session exists:', !!session, 'User:', session?.user?.email);

        // Handle SIGNED_IN event from OAuth callback
        if (event === 'SIGNED_IN' && session?.user) {
          console.log('User signed in via OAuth:', session.user.email);
          setUser(session.user);
          sessionChecked = true;
        }
        // Only redirect to signin if we're truly signed out and not during OAuth processing
        else if (event === 'SIGNED_OUT' && sessionChecked) {
          console.log('User signed out, redirecting to signin');
          setAuthType('signin');
          navigate('/signin');
        }
      });

      authSubscription = data.subscription;

      // Check for existing session after a delay to allow OAuth hash processing
      setTimeout(async () => {
       //if (!mounted || sessionChecked) return;

        console.log('Checking for existing session...', user);
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error getting session:', error);
        }

        console.log('Session check result:', !!session, 'User:', session?.user?.email);

        if (session?.user) {
          setUser(session.user);
          sessionChecked = true;
        } else {
          console.log('No session found, redirecting to signin');
          setAuthType('signin');
          navigate('/signin');
        }
      }, 1000); // Increased timeout to 1 second for OAuth processing
    };

    getCurrentUser();

    return () => {
      mounted = false;
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
    };
  }, [navigate]);

  // Populate name from OAuth user metadata for new OAuth users
  useEffect(() => {
    if (isPending && user?.user_metadata) {
      const meta = user.user_metadata;
      if (!firstName && meta.full_name) {
        const parts = meta.full_name.split(' ');
        setFirstName(parts[0] || '');
        setLastName(parts.slice(1).join(' ') || '');
      } else if (!firstName) {
        setFirstName(meta.first_name || meta.given_name || '');
        setLastName(meta.last_name || meta.family_name || '');
      }
    }
  }, [user, isPending]);

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, phone: formatPhoneNumber(value) }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renameWithOrgId = (file) => {
    const orgId = userProfile?.org_id || 'unknown';
    const newName = `${orgId}_${file.name}`;
    return new File([file], newName, { type: file.type });
  };

  const isNewFile = (file, existingPath) => {
    if (!file) return false;
    if (!existingPath) return true;
    const existingName = existingPath.split('/').pop();
    return file.name !== existingName;
  };

  // submit documents for operator
  const handleDocumentsSubmit = async () => {
    const newDo85 = isNewFile(do85File, organization?.branding?.do85Registration);
    const newPart135 = isNewFile(part135File, organization?.branding?.part135Certification);

    if (!newDo85 && !newPart135) {
      setCurrentStep('branding');
      return;
    }

    setLoading(true);

    try {
      let uploadedDo85Path = organization?.branding?.do85Registration || null;
      if (newDo85) {
        const uploaded = await apiClient.uploadFile(renameWithOrgId(do85File), "do85Registration");
        uploadedDo85Path = uploaded.file_path;
      }

      let uploadedPart135Path = organization?.branding?.part135Certification || null;
      if (newPart135) {
        const uploaded = await apiClient.uploadFile(renameWithOrgId(part135File), "part135Certification");
        uploadedPart135Path = uploaded.file_path;
      }

      await apiClient.updateOrganization(userProfile?.org_id, {
        name: organization?.name,
        type: organization?.type,
        subscription_tier: organization?.subscription_tier,
        branding: {
          ...organization?.branding,
          do85Registration: uploadedDo85Path,
          part135Certification: uploadedPart135Path,
        }
      });
      await queryClient.invalidateQueries({ queryKey: ["organization", userProfile?.org_id] });
      setDo85File(null);
      setPart135File(null);

      setCurrentStep('branding');
    } catch (err) {
      console.error('Document upload error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const PRESET_COLORS = [
    '#E53935', '#FF7043', '#FDD835', '#66BB6A', '#26A69A',
    '#29B6F6', '#5C6BC0', '#AB47BC', '#EC407A',
    '#BDBDBD', '#E0E0E0', '#9E9E9E', '#616161', '#212121',
  ];

  // submit branding form
  const handleBrandingSubmit = async () => {

    const newLogo = isNewFile(logoFile, organization?.branding?.logo);
    const colorChanged = brandColor !== organization?.branding?.color;

    if (!newLogo && !colorChanged) {
      setCurrentStep('contract');
      return;
    }

    setLoading(true);
    try {

      let branding = {
        ...organization?.branding,
        color: brandColor
      };

      if (newLogo) {

        let uploadedLogoPath = organization?.branding?.logo || null;
        const uploaded = await apiClient.uploadFile(renameWithOrgId(logoFile), "logo");
        uploadedLogoPath = uploaded.file_path;
        branding = {
          ...organization?.branding,
          color: brandColor,
          logo: uploadedLogoPath,
        };

      }

      await apiClient.updateOrganization(userProfile?.org_id, {
        name: organization?.name,
        type: organization?.type,
        subscription_tier: organization?.subscription_tier,
        branding: branding
      });
      
      await queryClient.invalidateQueries({ queryKey: ["organization", userProfile?.org_id] });
      setLogoFile(null);

      setCurrentStep('contract');
      
    } catch (err) {
      console.error('Branding setup error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // submit contract form
  const handleContractSubmit = async () => {

    const newContract = isNewFile(contractFile, organization?.branding?.contract);

    if (!newContract) {
      setCurrentStep('review');
      return;
    }

    setLoading(true);

    try {
      const uploaded = await apiClient.uploadFile(renameWithOrgId(contractFile), "contract");

      await apiClient.updateOrganization(userProfile?.org_id, {
        name: organization?.name,
        type: organization?.type,
        subscription_tier: organization?.subscription_tier,
        branding: {
          ...organization?.branding,
          contract: uploaded.file_path,
        }
      });
      
      await queryClient.invalidateQueries({ queryKey: ["organization", userProfile?.org_id] });
      setContractFile(null);

      setCurrentStep('review');
    } catch (err) {
      console.error('Contract upload error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // submit teams form
  const handleTeamSubmit = async () => {

    setLoading(true);

    try {

      // Send email invites to all added team members
      if (teamMembers.length > 0) {
        const invitePromises = teamMembers.map(member => {
          const encoded = encodeData({
            firstName: member.firstName,
            lastName: member.lastName,
            designation: member.designation,
            email: member.email,
            org_id: userProfile?.org_id,
            company: formData.companyName
          });
          const registrationUrl = `${window.location.origin}/register/team?d=${encodeURIComponent(encoded)}`;
          
          console.log("registrationUrl", registrationUrl);

          // sending invite to team members
          const messagePayload = {
            From: "tools@trustjet.ai",
            To: member.email,
            Subject: `You've been invited to join ${formData.companyName}`,
            HtmlBody: `<p>Hi ${member.firstName},</p>
              <p>You've been invited to join ${formData.companyName} as <strong>${member.designation}</strong>.</p>
              <p>Click the link below to complete your registration:</p>
              <p><a href="${registrationUrl}">Complete Registration</a></p>`,
          };

          return apiClient.sendMessage(messagePayload);

        });

        const results = await Promise.allSettled(invitePromises);
        const failed = results.filter(r => r.status === 'rejected' || r.value?.error);

        if (failed.length > 0 && failed.length < teamMembers.length) {
          toast.warning(`${teamMembers.length - failed.length} invite(s) sent. ${failed.length} failed.`);
        } else if (failed.length === teamMembers.length) {
          toast.error('Failed to send invites. Your account setup will still continue.');
        } else {
          toast.success(`${teamMembers.length} invite(s) sent successfully!`);
        }
      }

      // update user profile status
      await apiClient.updateUser(userProfile?.userprofile_id, {
        status: "ONBOARDING_COMPLETED"
      });

    } catch (err) {
      console.error('Team setup error:', err);
      toast.error(err.message);
    } finally {
      setLoading(false);
      setCurrentStep('review');
    }
  };

  const handleAddMember = () => {
    if (!newMember.firstName || !newMember.lastName || !newMember.designation || !newMember.email) {
      toast.error('Please fill in all fields.');
      return;
    }
    setTeamMembers(prev => [...prev, { id: Date.now(), ...newMember }]);
    setNewMember({ firstName: '', lastName: '', designation: '', email: '' });
    setShowAddMember(false);
  };

  const handleRemoveMember = (id) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  };

  // submit ogranization details
  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    setLoading(true);

      // Create organization
      if(!organization)
      {
        const org = await apiClient.createOrganization({
          name: formData.companyName,
          type: currentStep=='profile' ? 'BROKER' : 'OPERATOR',
          subscription_tier: "STARTER",
          billing_email: user.email,
          tax_id: null,
          address: { street: formData.address },
          settings: {
            pocTitle: formData.contactTitle,
            isSole: formData.soleProprietorship
          },
          payment_platforms: null,
          branding: {
            color: null,
            logo: null,
            contract: null,
            do85Registration: null,
            part135Certification: null
          }
        });

        // returned organization id
        const orgId = org.organization_id;

        // Check if user profile already exists
        let existingUser = null;
        try {
          existingUser = await apiClient.getUserByUserId(user.id);
        } catch {
          // User not found, will register
        }

        if (existingUser) {
          await apiClient.updateUser(existingUser.userprofile_id, {
            org_id: orgId,
            role: currentStep=="profile" ? "broker" : "operator",
            first_name: firstName,
            last_name: lastName,
            phone: formData.phone,
            status: "ONBOARDING_STARTED"
          });
        } else {
          await apiClient.registerUser({
            user_id: user.id,
            email: user.email,
            org_id: orgId,
            role: currentStep=="profile" ? "broker" : "operator",
            first_name: firstName,
            last_name: lastName,
            phone: formData.phone,
            status: "ONBOARDING_STARTED"
          });
        }

        // Refresh userProfile so org_id is available in the store
        await fetchUserProfile();
        setLoading(false);

      }else{

          const orgChanged =
            formData.companyName !== organization?.name ||
            formData.address !== organization?.address?.street ||
            formData.contactTitle !== organization?.settings?.pocTitle ||
            formData.soleProprietorship !== organization?.settings?.isSole;

          const phoneChanged = formData.phone !== userProfile?.phone;

          if (orgChanged) {
            await apiClient.updateOrganization(userProfile?.org_id, {
              name: formData.companyName,
              type: organization?.type,
              subscription_tier: organization?.subscription_tier,
              address: { street: formData.address },
              settings: {
                pocTitle: formData.contactTitle,
                isSole: formData.soleProprietorship
              },
            });
            await queryClient.invalidateQueries({ queryKey: ["organization", userProfile?.org_id] });
          }

          if (phoneChanged) {
            let existingUser = null;
            try {
              existingUser = await apiClient.getUserByUserId(user.id);
            } catch {
              // User not found
            }

            if(existingUser) {
              await apiClient.updateUser(existingUser.userprofile_id, {
                ...existingUser,
                phone: formData.phone,
              });
            }
          }

          setLoading(false);

      }

      setCurrentStep(isBroker ? 'branding' : 'documents');
  };

  const handleCompleteOnboarding = async() => {
     logout();
     await supabase.auth.signOut();
     navigate("/");
  };

  const getFileIcon = (file) => {
    if (file?.type === 'application/pdf') {
      return <RiFilePdf2Fill className="h-6 w-6 text-red-500" />;
    }
    if (file?.type?.startsWith('image/')) {
      return <RiFileImageFill className="h-6 w-6 text-purple-500" />;
    }
    return <RiFileTextLine className="h-6 w-6 text-gray-400" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const FileUploadBox = ({ label, file, onFileChange, inputId, acceptTypes, acceptLabel, existingPath }) => {
    const [dismissed, setDismissed] = useState(false);
    const accept = acceptTypes || '.jpg,.jpeg,.png,.pdf';
    const hint = acceptLabel || 'JPEG, PNG, and PDF, up to 50 MB.';
    const validMimes = acceptTypes === '.jpg,.jpeg,.png'
      ? ['image/jpeg', 'image/png']
      : acceptTypes === '.pdf'
        ? ['application/pdf']
        : ['image/jpeg', 'image/png', 'application/pdf'];

    const handleFile = (e) => {
      e.preventDefault();
      const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
      if (f) {
        if (!validMimes.includes(f.type)) {
          toast.error(`Please upload a ${hint.split(',').slice(0, -1).join(', ')} file.`);
          return;
        }
        if (f.size > 50 * 1024 * 1024) {
          toast.error('File must be under 50 MB.');
          return;
        }

        setDismissed(false);
        onFileChange(f);
      }
    };

    const handleRemove = () => {
      setDismissed(true);
      onFileChange(null);
    };

    const existingFileName = existingPath ? existingPath.split('/').pop() : null;
    console.log("step", currentStep);

    return (
      <div className="space-y-2">
        <Label>{label} <span className="text-red-500">*</span></Label>
        <input
          id={inputId}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFile}
        />
        {file ? (
          <div key="file" className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3">
            <div className="flex-shrink-0">
              {getFileIcon(file)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span>{formatFileSize(file.size)}</span>
                <span className="text-gray-300">·</span>
                <span className="flex items-center gap-1 text-green-600">
                  <RiCheckboxCircleLine size={14} />
                  Completed
                </span>
              </div>
            </div>
            <button
              type="button"
              className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1"
              onClick={handleRemove}
            >
              <RiDeleteBinLine size={18} />
            </button>
          </div>
        ) : existingPath && !dismissed ? (
          <div key="existing" className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3">
            <div className="flex-shrink-0">
              {/\.(jpe?g|png)$/i.test(existingPath) ? (
                <img src={`${process.env.REACT_APP_API_URL}/${existingPath}`} alt="" className="h-8 w-8 object-contain rounded" />
              ) : (
                <RiCheckboxCircleLine className="h-6 w-6 text-green-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{existingFileName}</p>
              <span className="flex items-center gap-1 text-xs text-green-600">
                <RiCheckboxCircleLine size={14} />
                Uploaded
              </span>
            </div>
            <label
              htmlFor={inputId}
              className="flex-shrink-0 text-sm text-primary-500 hover:underline cursor-pointer font-medium"
            >
              Replace
            </label>
          </div>
        ) : (
          <div
            key="dropzone"
            className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-gray-300 transition-colors"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFile}
            onClick={() => document.getElementById(inputId).click()}
          >
            <div className="space-y-1">
              <RiUploadCloud2Line className="h-8 w-8 text-gray-400 mx-auto" />
              <p className="text-sm text-gray-700 font-medium">Choose a file or drag & drop it here.</p>
              <p className="text-xs text-gray-400">{hint}</p>
              <button
                type="button"
                className="mt-2 px-4 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
                onClick={(e) => { e.stopPropagation(); document.getElementById(inputId).click(); }}
              >
                Browse File
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isResolvingStep) {
    return (
      <div className="flex flex-col h-full items-center justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${!isProfile ? "items-center" : ""} py-4`}>
      <div className="max-w-md w-full flex flex-col flex-1">
        {/* Header */}
        <div className="mb-6">
          {!isProfile && (
            <h2 className={`font-bold text-primary-500 mb-2 ${currentStep === 'review' ? 'text-2xl' : 'text-3xl'}`}>
              {currentStep === 'userType' ? 'What do you do?' : currentStep === 'profile' ? 'Organization Details' : currentStep === 'documents' ? 'Operator Documents' : currentStep === 'branding' ? 'Branding' : currentStep === 'team' ? 'Team members' : currentStep === 'review' ? 'Your account is under review' : 'Upload file'}
            </h2>
          )}
          {currentStep === 'profile' && (
            <p className="text-gray-600">
              Please provide your organization details to get started.
            </p>
          )}
          {/* {currentStep === 'team' && (
            <p className="text-gray-600">
              Invite team members to work with and assign member permissions.
            </p>
          )} */}
          {currentStep === 'review' && (
            <p className="text-gray-600">
              Your account creation is under review and will be completed soon. This review process usually takes up to 2 working days, but we'll notify you as soon as it's finished.
            </p>
          )}
        </div>

      {/* Steps container */}
        <div className="flex-1">
          {currentStep === 'userType' && (
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">I am a...</Label>
              <div className="space-y-2">
                {[
                  { value: "broker", label: "Broker" },
                  { value: "operator", label: "Operator" },
                ].map((option) => (
                  <label
                    key={option.value}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-700">{option.label}</span>
                    <input
                      type="checkbox"
                      value={option.value}
                      checked={userTypes.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUserTypes(prev => [...prev, option.value]);
                        } else {
                          setUserTypes(prev => prev.filter(type => type !== option.value));
                        }
                      }}
                      className="w-4 h-4 text-primary-500 border-gray-300 focus:ring-primary-500 rounded"
                    />
                  </label>
                ))}
              </div>
            </div>
          )}

          {currentStep === 'profile' && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="companyName">{currentStep=="profile" ? 'Brokerage' : ''} Company Name <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <RiUserLine size={16} />
                  </span>
                  <Input
                    id="companyName"
                    name="companyName"
                    type="text"
                    placeholder="HighAir Charter"
                    value={formData.companyName}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                    <RiMapPinLine size={16} />
                  </span>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder="24, Beacon Street, Massachusetts, Boston"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <div className="relative flex-shrink-0">
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      disabled={loading}
                      className="h-11 pl-3 pr-8 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none appearance-none"
                    >
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+61">🇦🇺 +61</option>
                      <option value="+91">🇮🇳 +91</option>
                    </select>
                  </div>
                  <div className="relative flex-1">
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(413) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="h-11 px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactTitle">Point-of-Contact Title <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <select
                    id="contactTitle"
                    name="contactTitle"
                    value={formData.contactTitle}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="h-11 w-full pl-4 pr-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed appearance-none"
                  >
                    <option value="">Select title</option>
                    <option value="ceo">Chief Executive Officer</option>
                    <option value="coo">Chief Operating Officer</option>
                    <option value="cfo">Chief Financial Officer</option>
                    <option value="director">Director of Operations</option>
                    <option value="manager">Manager</option>
                    <option value="owner">Owner</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="button"
                  className="flex-shrink-0 text-primary-500"
                  onClick={() => setFormData(prev => ({ ...prev, soleProprietorship: !prev.soleProprietorship }))}
                >
                  {formData.soleProprietorship
                    ? <RiCheckboxFill size={22} />
                    : <RiCheckboxBlankLine size={22} className="text-gray-400" />
                  }
                </button>
                <span className="text-sm text-gray-700">Sole Proprietorship</span>
              </div>
            </form>
          )}

          {currentStep === 'documents' && (
            <div className="space-y-6">
              <FileUploadBox
                label="Upload DO85 Registration"
                file={do85File}
                onFileChange={setDo85File}
                inputId="do85-upload"
                existingPath={organization?.branding?.do85Registration}
              />
              <FileUploadBox
                label="Upload Part 135 Certification"
                file={part135File}
                onFileChange={setPart135File}
                inputId="part135-upload"
                existingPath={organization?.branding?.part135Certification}
              />
            </div>
          )}

          {currentStep === 'branding' && (
            <div className="space-y-8">
              {/* Brand Color Picker */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Choose Brand Color <span className="text-red-500">*</span></Label>
                  <button
                    type="button"
                    className="text-sm text-primary-500 hover:underline font-medium flex items-center gap-1"
                    onClick={() => { setCustomColorInput(brandColor || '#000000'); setShowColorPicker(true); }}
                  >
                    <RiPencilLine size={14} />
                    Edit
                  </button>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        brandColor === color ? 'border-primary-500 scale-110 ring-2 ring-primary-200' : 'border-transparent hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setBrandColor(color)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors w-full justify-center"
                  onClick={() => { setCustomColorInput(brandColor || '#000000'); setShowColorPicker(true); }}
                >
                  <RiAddLine size={16} />
                  Add custom color
                </button>
              </div>

              {/* Logo Upload */}
              <FileUploadBox
                label="Upload Brokerage Logo"
                file={logoFile}
                onFileChange={setLogoFile}
                inputId="logo-upload"
                acceptTypes=".jpg,.jpeg,.png"
                acceptLabel="JPEG and PNG, up to 50 MB."
                existingPath={organization?.branding?.logo}
              />

              {/* Logo Preview */}
              {(logoFile || organization?.branding?.logo) && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Logo Preview</p>
                  <div className="flex items-center justify-center">
                    <img
                      src={logoFile ? URL.createObjectURL(logoFile) : `${process.env.REACT_APP_API_URL}${organization.branding.logo}`}
                      alt="Logo preview"
                      className="max-h-32 max-w-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Color Picker Modal */}
          {showColorPicker && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowColorPicker(false)} />
              <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Color picker</h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowColorPicker(false)}
                  >
                    <RiCloseLine size={22} />
                  </button>
                </div>

                {/* Color picker area */}
                <div className="color-picker-wrapper">
                  <HexColorPicker color={customColorInput} onChange={setCustomColorInput} style={{ width: '100%' }} />
                </div>

                {/* Hex input */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="text-xs text-gray-500 mb-1 block">Hex</label>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded border border-gray-200 flex-shrink-0" style={{ backgroundColor: customColorInput }} />
                      <input
                        type="text"
                        value={customColorInput.replace('#', '').toUpperCase()}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
                          if (val.length <= 6) setCustomColorInput(`#${val}`);
                        }}
                        className="w-full h-9 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary-300"
                        maxLength={6}
                      />
                    </div>
                  </div>
                </div>

                {/* Recommended Colors */}
                <div className="space-y-2">
                  <label className="text-xs text-gray-500">Recommended Colors</label>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_COLORS.slice(0, 10).map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-7 h-7 rounded-full border-2 transition-all ${
                          customColorInput.toLowerCase() === color.toLowerCase() ? 'border-primary-500 ring-2 ring-primary-200' : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setCustomColorInput(color)}
                      />
                    ))}
                  </div>
                </div>

                {/* Add button */}
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-colors rounded-full"
                  onClick={() => { setBrandColor(customColorInput); setShowColorPicker(false); }}
                >
                  <RiPaletteLine size={18} />
                  Add custom color
                </button>
              </div>
            </div>
          )}

          {currentStep === 'contract' && (
            <div className="space-y-6">
              <FileUploadBox
                label="Upload Standard Charter Contract PDF"
                file={contractFile}
                onFileChange={setContractFile}
                inputId="contract-upload"
                acceptTypes=".pdf"
                acceptLabel="PDF, up to 50 MB."
                existingPath={organization?.branding?.contract}
              />
            </div>
          )}

          {/* {currentStep === 'team' && (
            <div className="space-y-4">
              <button
                type="button"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-primary-500 rounded-full font-medium text-sm transition-colors"
                onClick={() => setShowAddMember(true)}
              >
                <RiTeamLine size={18} />
                Add member
              </button>

              {teamMembers.map((member) => (
                <div key={member.id} className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-50 text-primary-500 font-medium text-sm flex-shrink-0">
                    {member.firstName[0]}{member.lastName[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{member.firstName} {member.lastName}</p>
                    <p className="text-xs text-gray-500 truncate">{member.designation} &middot; {member.email}</p>
                  </div>
                  <button
                    type="button"
                    className="flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1"
                    onClick={() => handleRemoveMember(member.id)}
                  >
                    <RiDeleteBinLine size={18} />
                  </button>
                </div>
              ))}
            </div>
          )} */}

          {currentStep === 'review' && (
            <div className="flex items-center justify-center py-16">
              <div className="w-28 h-28 rounded-full bg-gray-100 flex items-center justify-center">
                <RiHourglass2Fill className="w-12 h-12 text-primary-500" />
              </div>
            </div>
          )}

          {/* Add Member Modal */}
          {showAddMember && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-black/40" onClick={() => setShowAddMember(false)} />
              <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Add new member</h3>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowAddMember(false)}
                  >
                    <RiCloseLine size={22} />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>First Name <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      placeholder="John"
                      value={newMember.firstName}
                      onChange={(e) => setNewMember(prev => ({ ...prev, firstName: e.target.value }))}
                      className="h-11 px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Last Name <span className="text-red-500">*</span></Label>
                    <Input
                      type="text"
                      placeholder="Doe"
                      value={newMember.lastName}
                      onChange={(e) => setNewMember(prev => ({ ...prev, lastName: e.target.value }))}
                      className="h-11 px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Designation <span className="text-red-500">*</span></Label>
                  <Input
                    type="text"
                    placeholder="e.g. Manager"
                    value={newMember.designation}
                    onChange={(e) => setNewMember(prev => ({ ...prev, designation: e.target.value }))}
                    className="h-11 px-4 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <Label>Email Address <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <RiMailLine size={16} />
                    </span>
                    <Input
                      type="email"
                      placeholder="example@email.com"
                      value={newMember.email}
                      onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                      className="h-11 pl-10 bg-white border-2 border-gray-200 text-gray-700 rounded-lg focus:outline-none"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-500 rounded-full font-medium text-sm transition-colors disabled:opacity-50 rounded-full"
                  disabled={!newMember.firstName || !newMember.lastName || !newMember.designation || !newMember.email}
                  onClick={handleAddMember}
                >
                  <RiTeamLine size={18} />
                  Add Member
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Bottom Buttons */}
        <div className="flex space-x-4 mt-auto pt-6">
          {currentStep !== 'userType' && currentStep !== 'review' && (isPending || currentStep !== 'profile') && (
            <Button
              type="button"
              variant="secondary"
              mode="stroke"
              className="flex-1 !border-border-light !font-normal rounded-full"
              disabled={loading}
              onClick={() => setCurrentStep(
                currentStep === 'profile' ? 'userType' :
                currentStep === 'team' ? 'contract' :
                currentStep === 'contract' ? 'branding' :
                currentStep === 'branding' ? (isBroker ? 'profile' : 'documents') : 'profile'
              )}
            >
              Back
            </Button>
          )}
          {currentStep === 'review' ? (
            <Button
              type="button"
              className="flex-1 !font-normal bg-primary-500 hover:bg-primary-600 w-full rounded-full"
              onClick={handleCompleteOnboarding}
            >
              Got it
            </Button>
          ) : (
            <Button
              type="button"
              className={`flex-1 !font-normal bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-full ${currentStep === 'userType' || currentStep === 'profile' ? 'w-full' : ''}`}
              disabled={
                loading ||
                (currentStep === 'userType' && userTypes.length === 0) ||
                (currentStep === 'profile' && (!formData.companyName || !formData.address || !formData.phone || !formData.contactTitle)) ||
                (currentStep === 'documents' && ((!do85File && !organization?.branding?.do85Registration) || (!part135File && !organization?.branding?.part135Certification))) ||
                (currentStep === 'branding' && (!brandColor || (!logoFile && !organization?.branding?.logo))) ||
                (currentStep === 'contract' && (!contractFile && !organization?.branding?.contract))
              }
              onClick={
                currentStep === 'userType' ? () => setCurrentStep('profile') :
                currentStep === 'profile' ? handleSubmit :
                currentStep === 'documents' ? handleDocumentsSubmit :
                currentStep === 'branding' ? handleBrandingSubmit :
                currentStep === 'contract' ? handleContractSubmit :
                handleTeamSubmit
              }
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin h-4 w-4 border-b-2 border-white mr-2"></div>
                  {currentStep === 'team' ? 'Submitting...' : currentStep === 'profile' ? 'Saving...' : currentStep === 'branding' ? 'Saving...' : 'Uploading...'}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {currentStep === 'team' ? 'Skip and Submit' : 'Next'}
                  <RiArrowRightLine className="ml-2 h-4 w-4" />
                </div>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
